/**
 * 스케줄 등록 기간 관리 (오너용)
 * - 전체 열기/닫기 토글
 * - 등록 기간 설정 (시작일/종료일)
 * - 학생 개인별 오버라이드
 */
import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';

interface StudentOverride {
  student_id: string;
  name: string;
  override: string | null;
  plan_status: string | null;
  effective_from_date?: string | null;
  has_pending_modification?: boolean;
}

export default function ScheduleRegistrationSettings() {
  const [enabled, setEnabled] = useState(false);
  const [alwaysOpen, setAlwaysOpen] = useState(false);
  const [startDay, setStartDay] = useState(25);
  const [endDay, setEndDay] = useState(5);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [students, setStudents] = useState<StudentOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [yearMonth, setYearMonth] = useState(() => {
    // ⭐ 대상 월 기본값 = 당월(현재 달). (기존엔 익월로 잡혀 있었음)
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // 🔎 학생 검색 (이름)
  const [search, setSearch] = useState('');
  // 📜 변경·승인 이력 모달
  const [historyFor, setHistoryFor] = useState<{ id: string; name: string } | null>(null);
  const [historyRows, setHistoryRows] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = async (studentId: string, name: string) => {
    setHistoryFor({ id: studentId, name });
    setHistoryLoading(true);
    setHistoryRows([]);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/owner/student-schedules/student/${studentId}/schedule-history`, { headers });
      if (res.ok) setHistoryRows((await res.json()).history || []);
    } catch (e) {
      console.error('이력 로드 실패:', e);
    }
    setHistoryLoading(false);
  };
  const fmtHistTime = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', dateStyle: 'short', timeStyle: 'short' }) : '';

  useEffect(() => {
    loadSettings();
    loadOverrides();
  }, [yearMonth]);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/monthly-plans/registration/settings`, { headers });
      if (res.ok) {
        const data = await res.json();
        setEnabled(data.data.schedule_reg_enabled);
        setAlwaysOpen(!!data.data.schedule_reg_always_open);
        setStartDay(data.data.schedule_reg_start_day);
        setEndDay(data.data.schedule_reg_end_day);
      }
    } catch (e) {
      console.error('설정 로드 실패:', e);
    }
    setLoading(false);
  };

  const loadOverrides = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/monthly-plans/registration/student-overrides?year_month=${yearMonth}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setStudents(data.data);
      }
    } catch (e) {
      console.error('오버라이드 로드 실패:', e);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/monthly-plans/registration/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          schedule_reg_enabled: enabled,
          schedule_reg_start_day: startDay,
          schedule_reg_end_day: endDay,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setSaveError(typeof body?.detail === 'string' ? body.detail : '저장하지 못했습니다.');
      }
    } catch (e) {
      setSaveError('저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    }
    setSaving(false);
  };

  const toggleAlwaysOpen = async () => {
    const newVal = !alwaysOpen;
    setAlwaysOpen(newVal);
    setSaveError(null);
    try {
      await fetch(`${API_BASE_URL}/api/v1/monthly-plans/registration/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ schedule_reg_always_open: newVal }),
      });
    } catch (e) {
      setAlwaysOpen(!newVal);
    }
  };

  const toggleEnabled = async () => {
    const newVal = !enabled;
    setEnabled(newVal);
    try {
      await fetch(`${API_BASE_URL}/api/v1/monthly-plans/registration/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ schedule_reg_enabled: newVal }),
      });
    } catch (e) {
      setEnabled(!newVal);
    }
  };

  const setOverride = async (studentId: string, override: string | null) => {
    try {
      await fetch(
        `${API_BASE_URL}/api/v1/monthly-plans/registration/student-override?year_month=${yearMonth}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ student_id: studentId, override }),
        }
      );
      loadOverrides();
    } catch (e) {
      console.error('오버라이드 설정 실패:', e);
    }
  };

  // 어느 달 스케줄을 언제부터 언제까지 낼 수 있는지 (전월 startDay ~ 당월 endDay)
  const getWindow = (y: number, m: number) => {
    const prevY = m === 1 ? y - 1 : y;
    const prevM = m === 1 ? 12 : m - 1;
    const clamp = (yy: number, mm: number, d: number) =>
      Math.min(d, new Date(yy, mm, 0).getDate());
    const from = new Date(prevY, prevM - 1, clamp(prevY, prevM, startDay));
    const to = new Date(y, m - 1, clamp(y, m, endDay));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return {
      label: `${m}월 스케줄`,
      text: `${prevM}/${from.getDate()} ~ ${m}/${to.getDate()}`,
      open: enabled && today >= from && today <= to,
    };
  };

  const nowYm = (() => {
    const n = new Date();
    return { y: n.getFullYear(), m: n.getMonth() + 1 };
  })();
  const windows = alwaysOpen
    ? []
    : [
        getWindow(nowYm.y, nowYm.m),
        getWindow(nowYm.m === 12 ? nowYm.y + 1 : nowYm.y, nowYm.m === 12 ? 1 : nowYm.m + 1),
      ];

  if (loading) return <div className="text-center py-8 text-gray-400">로딩 중...</div>;

  return (
    <div className="space-y-6">
      {/* 전체 토글 + 기간 설정 */}
      <div className="bg-white rounded-xl border p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">스케줄 등록</h3>
            <p className="text-sm text-gray-500 mt-1">학생들의 월간 스케줄 등록을 열거나 닫습니다</p>
          </div>
          <button
            onClick={toggleEnabled}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
              enabled ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {enabled && (
          <div className="border-t pt-4 mt-4">
            {/* 상시 열어두기 */}
            <div className="flex items-center justify-between mb-4 px-3 py-2.5 rounded-lg bg-gray-50 border">
              <div>
                <p className="text-sm font-medium text-gray-800">상시 열어두기</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  켜면 날짜 제한 없이 언제든 스케줄을 낼 수 있습니다. 아래 등록 기간은 적용되지 않습니다.
                </p>
              </div>
              <button
                onClick={toggleAlwaysOpen}
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${
                  alwaysOpen ? 'bg-green-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    alwaysOpen ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <p className={`text-sm font-medium mb-3 ${alwaysOpen ? 'text-gray-400' : 'text-gray-700'}`}>
              등록 기간{alwaysOpen && <span className="ml-2 text-xs font-normal">(상시 열어두기가 켜져 있어 적용되지 않습니다)</span>}
            </p>
            <div className={`flex items-center gap-3 ${alwaysOpen ? 'opacity-40 pointer-events-none' : ''}`}>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">전월</span>
                <input
                  type="number"
                  min={1}
                  max={31}
                  value={startDay}
                  onChange={(e) => setStartDay(Number(e.target.value))}
                  className="w-16 border rounded-lg px-2 py-1.5 text-center text-sm"
                />
                <span className="text-sm text-gray-500">일</span>
              </div>
              <span className="text-gray-400">~</span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">당월</span>
                <input
                  type="number"
                  min={1}
                  max={31}
                  value={endDay}
                  onChange={(e) => setEndDay(Number(e.target.value))}
                  className="w-16 border rounded-lg px-2 py-1.5 text-center text-sm"
                />
                <span className="text-sm text-gray-500">일</span>
              </div>
              <button
                onClick={saveSettings}
                disabled={saving}
                className="ml-auto px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
            {saveError && (
              <p className="mt-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 leading-relaxed">
                {saveError}
              </p>
            )}

            {alwaysOpen ? (
              <p className="mt-3 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-xs text-green-800">
                지금은 <b>어느 달이든 언제나</b> 스케줄을 낼 수 있습니다.
              </p>
            ) : (
              <div className="mt-3 space-y-1">
                {windows.map((w) => (
                  <div key={w.label} className="flex items-center gap-2 text-xs">
                    <span className="w-20 text-gray-500">{w.label}</span>
                    <span className="text-gray-700">{w.text}</span>
                    <span
                      className={`px-1.5 py-0.5 rounded-full font-semibold ${
                        w.open ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {w.open ? '지금 등록 가능' : '아직/이미 닫힘'}
                    </span>
                  </div>
                ))}
                <p className="text-[11px] text-gray-400 pt-1">
                  종료일은 시작일보다 앞이어야 합니다(예: 전월 25일 ~ 당월 5일). 그렇지 않으면 다음 달 기간과 겹칩니다.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 학생 개별 관리 */}
      <div className="bg-white rounded-xl border p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">학생별 등록 권한 <span className="text-[11px] font-normal text-amber-600">(예외 기능 — 일반적으로 사용 권장 안 함)</span></h3>
            <p className="text-sm text-gray-500 mt-1">
              대상 월:
              <input
                type="month"
                value={yearMonth}
                onChange={(e) => setYearMonth(e.target.value)}
                className="ml-2 border rounded px-2 py-0.5 text-sm"
              />
            </p>
          </div>
        </div>

        <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[12px] text-amber-800 leading-relaxed">
          ⚠️ 이 기능은 <b>기본 흐름(학생 신청 → 학부모 동의 → 독서실 승인, 수정기간 내 1회)</b>을 벗어나는 예외 처리입니다.
          평소엔 사용하지 마시고, 수정기간이 지났는데 부득이하게 특정 학생만 한 번 더 열어줘야 할 때만 쓰세요.
          <br />· <b>허용</b>: 그 학생만 1회 수정 가능(수정·승인되면 자동으로 기본으로 돌아감) · <b>차단</b>: 그 학생만 수정 금지
        </div>
        {/* 🔎 학생 검색 */}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="🔎 학생 이름 검색"
          className="w-full mb-3 border rounded-lg px-3 py-2 text-sm"
        />
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {students.filter((s) => !search.trim() || (s.name || '').toLowerCase().includes(search.trim().toLowerCase())).map((s) => {
            const isApproved = s.plan_status === 'approved';
            const isReeditAllowed = isApproved && s.override === 'allowed';
            const isReeditInProgress = s.has_pending_modification;

            return (
              <div
                key={s.student_id}
                className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50"
              >
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900">{s.name}</span>
                    {s.plan_status && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        s.plan_status === 'approved' ? 'bg-green-100 text-green-700' :
                        s.plan_status === 'pending' ? 'bg-orange-100 text-orange-700' :
                        s.plan_status === 'draft' ? 'bg-gray-100 text-gray-600' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {s.plan_status === 'approved' ? '승인' : s.plan_status === 'pending' ? '대기' : s.plan_status === 'draft' ? '작성중' : '거절'}
                      </span>
                    )}
                    {isReeditAllowed && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">
                        수정 허용 중
                      </span>
                    )}
                    {isReeditInProgress && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold">
                        수정중
                      </span>
                    )}
                  </div>
                  {s.effective_from_date && (
                    <span className="text-[10px] text-gray-400">
                      {s.effective_from_date}부터 적용
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* 📜 이력 보기 (누가·언제 승인/변경/허용) */}
                  <button
                    onClick={() => openHistory(s.student_id, s.name)}
                    className="text-xs px-2 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 font-medium"
                    title="변경·승인 이력"
                  >
                    📜 이력
                  </button>
                  {/* 승인 상태일 때 "수정 1회 허용" 빠른 버튼 (예외 기능) */}
                  {isApproved && !isReeditAllowed && (
                    <button
                      onClick={() => {
                        if (window.confirm(`[예외 처리] ${s.name} 학생에게 수정을 1회 허용하시겠습니까?\n\n· 일반적으로는 사용하지 않는 기능입니다.\n· 학생이 수정 후 승인받으면 자동으로 기본(권한 없음)으로 돌아갑니다.`)) {
                          setOverride(s.student_id, 'allowed');
                        }
                      }}
                      className="text-xs px-2 py-1 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-medium"
                    >
                      수정 1회 허용
                    </button>
                  )}
                  <select
                    value={s.override || 'default'}
                    onChange={(e) => {
                      const val = e.target.value === 'default' ? null : e.target.value;
                      setOverride(s.student_id, val);
                    }}
                    className={`text-sm border rounded-lg px-2 py-1 ${
                      s.override === 'allowed' ? 'bg-green-50 text-green-700 border-green-300' :
                      s.override === 'blocked' ? 'bg-red-50 text-red-700 border-red-300' :
                      'bg-white text-gray-600'
                    }`}
                  >
                    <option value="default">기본</option>
                    <option value="allowed">허용</option>
                    <option value="blocked">차단</option>
                  </select>
                </div>
              </div>
            );
          })}
          {students.length === 0 && (
            <p className="text-center py-8 text-gray-400">학생이 없습니다</p>
          )}
          {students.length > 0 && students.filter((s) => !search.trim() || (s.name || '').toLowerCase().includes(search.trim().toLowerCase())).length === 0 && (
            <p className="text-center py-8 text-gray-400">'{search}' 검색 결과가 없습니다</p>
          )}
        </div>
      </div>

      {/* 📜 변경·승인 이력 모달 */}
      {historyFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setHistoryFor(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="font-bold text-gray-900">{historyFor.name} — 변경·승인 이력</h3>
              <button onClick={() => setHistoryFor(null)} className="text-gray-400 hover:text-gray-700 text-xl leading-none">✕</button>
            </div>
            <div className="px-5 py-4 overflow-y-auto">
              <p className="text-[11px] text-gray-400 mb-3">누가·언제·무엇을 했는지 기록입니다. (최근 순)</p>
              {historyLoading ? (
                <p className="text-center py-8 text-gray-400 text-sm">불러오는 중…</p>
              ) : historyRows.length === 0 ? (
                <p className="text-center py-8 text-gray-400 text-sm">기록된 이력이 없습니다</p>
              ) : (
                <ol className="space-y-3">
                  {historyRows.map((h, i) => (
                    <li key={i} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <span className={`w-2.5 h-2.5 rounded-full mt-1 ${
                          h.action === 'approve' ? 'bg-green-500' :
                          h.action === 'reject' ? 'bg-red-500' :
                          h.action === 'clear_all' ? 'bg-red-500' :
                          h.action.startsWith('override') ? 'bg-blue-500' : 'bg-gray-400'
                        }`} />
                        {i < historyRows.length - 1 && <span className="w-px flex-1 bg-gray-200 mt-1" />}
                      </div>
                      <div className="pb-1">
                        <div className="text-sm font-semibold text-gray-800">
                          {h.action_label}
                          {(h.action === 'update' || h.action === 'create' || h.action === 'clear_all') && typeof h.after_day_count === 'number' && (
                            <span className="ml-1 text-xs font-normal text-gray-400">({h.before_day_count}일 → {h.after_day_count}일)</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {h.actor_role_label}{h.actor_name ? ` · ${h.actor_name}` : ''} · {fmtHistTime(h.created_at)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
